import CTNFilterBar, { CTNFilterButton, CTNFilterDropdown, CTNFilterSearch } from '@ctn/ui/components/ctn-filter-bar';
import CTNTable from '@ctn/ui/components/ctn-table';
import { type CTNFilterBarRef } from '@ctn/ui/types/ctn-filter-bar';
import { type CTNTableComponentRowRef } from '@ctn/ui/types/ctn-table';
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import {
	type ISolicitudListadoPayload,
	type ISolicitudResponse,
	postSolicitudListado,
} from '@/services/solicitudes.service';

import { PAGE_SIZE } from '@/constants/table.constants';
import { SOLICITUD_ESTADOS } from '@/constants/tramite.constants';
import { type Action, makeActions } from '@/helpers/table';
import { type ISolicitud } from '@/interfaces/model/solicitudes.interface';

import getColumns from './columns';
import { defaultValues, type FilterFormData, getSchema, PAYLOAD_DEFAULT, PAYLOAD_PAGINATION } from './config';

type SolicitudesListadoProps = {
	readonly onView: (rowData: ISolicitud) => void;
	readonly onHistorial: (rowData: ISolicitud) => void;
	readonly onEdit: (rowData: ISolicitud) => void;
	readonly defaultFilter?: Partial<FilterFormData>;
};

type Pagination = Pick<ISolicitudListadoPayload, 'limit' | 'start'>;

const SolicitudesListado = ({
	onView,
	onEdit,
	onHistorial,
	defaultFilter = {},
}: SolicitudesListadoProps): ReactElement => {
	const filterBarRef = useRef<CTNFilterBarRef>(null);
	const {
		maestrosGeneric: { tipoTramite: tipoTramiteMaestro, estadoSolicitud },
	} = useGlobalContext();

	const tableRef = useRef<CTNTableComponentRowRef<ISolicitud>>(null);
	const { loading, triggerFetch } = useApiData({ loading: ['listado'] as const });
	const [loadingDefaultFilters, setLoadingDefaultFilters] = useState(false);
	const [dataTable, setDataTable] = useState<ISolicitudResponse>({ totalResults: 10, listado: [] });
	const [pagination, setPagination] = useState<Pagination>(PAYLOAD_PAGINATION);
	const location = useLocation();
	const { navigateData, setNavigateData } = usePageContext();
	const [isReady, setIsReady] = useState(false);
	const isInitialLoad = useRef(true);

	const { watch, control, reset, setValue } = useForm<FilterFormData>({
		resolver: getSchema(),
		mode: 'onChange',
		defaultValues: { ...defaultValues() },
	});

	const search = watch('search');
	const tipoBusqueda = search?.[0]?.key ?? defaultValues().tipoBusqueda;
	const listaIdsTipos = watch('listaIdsTipos');
	const listaIdsSubtipos = watch('listaIdsSubtipos');
	const listaIdsEstados = watch('listaIdsEstados');

	const getData = async (payload: Pagination & Partial<ISolicitudListadoPayload>) => {
		const { data } = await triggerFetch({
			fetch: postSolicitudListado,
			payload: { ...PAYLOAD_DEFAULT, ...payload },
			name: 'listado',
		});
		if (data) {
			setDataTable(data);
		}
	};

	useEffect(() => {
		if (!navigateData?.filter?.urls?.some(path => location.pathname.includes(path))) {
			setIsReady(true);
			if (Object.values(defaultFilter).length > 0 && !defaultFilter.listaIdsEstados?.some((it) => it === 'undefined')) {
				filterBarRef?.current?.addFilters(defaultFilter);
				setLoadingDefaultFilters(true);
				isInitialLoad.current = false;
				return;
			}

			const { filter, ...resto } = navigateData ?? {};
			setNavigateData(resto);
			getData(mapPayload(pagination, { tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdsTipos, listaIdsEstados, listaIdsSubtipos }));
			return;
		}

		const {data} = navigateData.filter;
		if (!data) {
			getData(mapPayload(pagination, { tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdsTipos, listaIdsEstados, listaIdsSubtipos }));
			setIsReady(true);
			return;
		}
		reset({
			...defaultValues(),
			search: [{ key: data.tipoBusqueda ?? defaultValues().tipoBusqueda, search: data.numeroTramiteExterno }],
			listaIdsEstados: (data.listaIdsEstadoSolicitud ?? []).map((id: number) => String(id)),
			listaIdsTipos: (data.listaIdsTipoTramite ?? []).map((id: number) => String(id)),
		});

		filterBarRef.current?.addFilters({
			search: data.numeroTramiteExterno ? [{ key: data.tipoBusqueda ?? defaultValues().tipoBusqueda, search: data.numeroTramiteExterno }] : [],
			listaIdsEstados: (data.listaIdsEstadoSolicitud ?? []).map((id: number) => String(id)),
			listaIdsTipos: (data.listaIdsTipoTramite ?? []).map((id: number) => String(id)),
		});
		const { filter, ...resto } = navigateData ?? {};
		setNavigateData(resto);
		setIsReady(true);
		isInitialLoad.current = false;
	}, []); 

	const mapPayload = (pagination: Pagination, filter: FilterFormData): ISolicitudListadoPayload => ({
		...pagination,
		[tipoBusqueda]: filter.busqueda ? filter.busqueda : undefined,
		listaIdsTipoTramite: filter.listaIdsTipos?.length > 0 ? filter.listaIdsTipos.map(Number) : [],
		listaIdsEstadoSolicitud: filter.listaIdsEstados?.length > 0 ? filter.listaIdsEstados.map(Number) : [],
	});

	const onPageChange = (start: number) => {
		let newPagination = {
			start: PAGE_SIZE.OPERACIONES * (start - 1),
			limit: PAGE_SIZE.OPERACIONES,
		};

		if (start === 1) {
			newPagination = {
				start: 0,
				limit: PAGE_SIZE.OPERACIONES,
			};
		}
		getData(
			mapPayload(newPagination, { tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdsTipos, listaIdsEstados, listaIdsSubtipos }),
		);
		setPagination(newPagination);
	};

	const columns = useMemo(() => {
		const actions: Array<Action<ISolicitud>> = [
			{
				width: 100,
				label: 'Editar',
				command: onEdit,
				hideIf: ({ estadoSolicitud }) => estadoSolicitud.id !== SOLICITUD_ESTADOS.BORRADOR,
			},
			{
				width: 110,
				label: 'Ver detalle',
				command: onView,
				hideIf: ({ estadoSolicitud }) => estadoSolicitud.id === SOLICITUD_ESTADOS.BORRADOR,
			}
		];

		const columnsWithOutActions = getColumns();

		const actionsColumn = makeActions<ISolicitud>({ data: dataTable.listado, actions });

		return [...columnsWithOutActions, actionsColumn];
	}, [dataTable.listado]);

	useEffect(() => {
		if (!isReady) return;
		if (isInitialLoad.current) {
			isInitialLoad.current = false;
			return;
		}
		const payload = mapPayload(pagination, { tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdsTipos, listaIdsEstados, listaIdsSubtipos });
		getData(payload);
		setNavigateData({ filter: 
			{ urls: [
				location.pathname, 
				'/finobr',
				'/com',
				'/ont',
				'/divhor',
				'/onc'
			], data: payload }});
	}, [isReady, search, loadingDefaultFilters, tipoBusqueda, JSON.stringify(listaIdsTipos), JSON.stringify(listaIdsEstados)]);

	return (
		<section key={location.pathname} className="u-mt-24">
			<CTNFilterBar
				ref={filterBarRef}
				tagBar
				show
				className="c-pnp-filter-bar c-pnp-filter-bar--wrap"
				control={control as any}
				setValue={setValue as any}
				reset={reset as any}
			>
				<CTNFilterSearch
					name="search" placeholder="Nº solicitud" label="Buscar"
					tag="Búsqueda"
					options={[{ label: 'Nº solicitud', value: 'numeroTramiteExterno' }]} />
				<CTNFilterDropdown
					multiple
					selectAll
					showArrow={false}
					label="Estado"
					valueFormatter={() => 'Estado'}
					placeholder="Estado"
					name="listaIdsEstados"
					tag="Estado"
					options={estadoSolicitud?.map((estado) => ({
						label: estado.descripcion,
						value: String(estado.id),
					}))}
				/>
				<CTNFilterDropdown
					multiple
					selectAll
					showArrow={false}
					label="Tipo de negocio jurídico"
					valueFormatter={() => 'Tipo de negocio jurídico'}
					placeholder="Tipo de negocio jurídico"
					name="listaIdsTipos"
					tag="Tipo de negocio jurídico"
					options={tipoTramiteMaestro?.map(({ descripcion, id }) => ({
						label: descripcion,
						value: String(id),
					}))}
				/>
				<CTNFilterButton
					name="clearAll"
					label="Reiniciar filtros"
					tag="Limpiar"
					icon={[{ icon: 'restartCircle', position: 'left', color: 'blue-60' }]}
					onClick={() => filterBarRef.current?.resetFilter()}
				/>
			</CTNFilterBar>
			<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-mt-24 ]">
				<div className="c-pnp-box__body">
					<CTNTable
						ref={tableRef}
						manualPagination
						idData="id"
						loading={loading.listado}
						loadingConf={{ spinner: true }}
						columns={columns}
						data={dataTable.listado}
						totalData={dataTable.totalResults}
						manualPageCount={dataTable.totalResults / PAGE_SIZE.OPERACIONES}
						pageSize={PAGE_SIZE.OPERACIONES}
						className="c-pnp-table"
						onPageChange={onPageChange}
					/>
				</div>
			</div>
		</section>
	);
};

export default SolicitudesListado;
